// Sugalarm — Cycle 362: the "still outstanding" reminder chip.
//
// This is the affordance that lets the cycle-361 10-minute auto-dismiss be
// safe. The fullscreen takeover clears itself after 10 minutes (operator
// directive), but the OCCURRENCE stays outstanding server-side until someone
// acknowledges it — so a never-Closed reminder leaves a visible trace instead
// of vanishing. A missed insulin dose is no longer indistinguishable from a
// handled one.
//
// Deliberately NOT a takeover:
//   * a slim bottom strip, so it cannot bury anything;
//   * z-index 30 — BELOW every alarm surface (banner strip 12 inside the
//     kiosk frame, AlarmSilenceGuard 60, UrgentOverlay 100) and below the
//     reminder takeover (9000). App.tsx additionally renders null while an
//     alarm episode is live, so it never competes with an alarm at all;
//   * one Close, which is the DURABLE ack (`/api/reminders/fires/{id}/dismiss`).
//
// It names the time the reminder was FOR (`formatReminderTime`), never the
// current clock — see lib/reminderLabel.ts for why that is load-bearing.

import type { ReminderFire } from "../../lib/types";
import { formatReminderTime } from "../../lib/reminderLabel";
import styles from "./MissedReminderBanner.module.css";

export interface MissedReminderBannerProps {
  /** Outstanding occurrences, newest first. The newest is shown; the rest are
   *  counted, so a household that never taps Close sees one strip, not ten. */
  fires: ReminderFire[];
  /** Honours the user's existing `display_24h` setting. */
  h24: boolean;
  /** Durable acknowledgement of `fire`. */
  onDismiss: (fire: ReminderFire) => void;
  /** Kiosk sizing (1024×600 / 1024×640 fit). */
  isKiosk?: boolean;
}

export function MissedReminderBanner({
  fires,
  h24,
  onDismiss,
  isKiosk = false,
}: MissedReminderBannerProps) {
  const fire = fires[0];
  // Guard on the value, not the length — `noUncheckedIndexedAccess` makes
  // `fires[0]` possibly-undefined, and this also covers a caller that filters
  // the list down to empty.
  if (fire == null) return null;
  const extra = fires.length - 1;
  const when = formatReminderTime(fire.time_of_day, h24);

  return (
    <div
      className={`${styles.strip}${isKiosk ? ` ${styles.kiosk}` : ""}`}
      role="status"
      aria-live="polite"
    >
      <span className={styles.icon} aria-hidden="true">
        ⏰
      </span>
      <span className={styles.text}>
        <span className={styles.when}>{when}</span>
        <span className={styles.sep} aria-hidden="true">
          ·
        </span>
        <span className={styles.message}>{fire.message}</span>
        {extra > 0 ? (
          <span className={styles.more}>{` +${extra} more`}</span>
        ) : null}
      </span>
      <button
        type="button"
        className={styles.close}
        aria-label={`Close reminder for ${when}`}
        onClick={() => onDismiss(fire)}
      >
        Close
      </button>
    </div>
  );
}
